import { useRef, useState } from 'react'
import { App } from 'antd'
import { statusOf } from '@sportninja/common/features/registration/teamBudgetPayment/utils/checkoutErrors'

const REFUSED_FALLBACK =
  "We couldn't start over right now. Check your connection and try again."

/** The server's own reason for a refusal (a 4xx says why); anything else reads generic. */
const refusalMessage = (error: unknown): string => {
  const status = statusOf(error)
  const text = (error as { message?: unknown } | null)?.message

  return status !== null &&
    status >= 400 &&
    status < 500 &&
    typeof text === 'string' &&
    text !== ''
    ? text
    : REFUSED_FALLBACK
}

interface UseStartOverArgs {
  /** Deletes the registration session (the context's, or `sessionId`). */
  resetSession: (sessionId?: string) => Promise<void>
  /** Delete this session instead of the context's. */
  sessionId?: string
  /** Runs once the session is gone, before the reload (e.g. logging out). */
  then?: () => void
  /** Starts the wizard again; the default reloads the page. */
  reload?: () => void
}

/**
 * SN-8441 / SN-8442 — Start Over: delete this registration session, THEN reload.
 *
 * It used to fire the delete and reload half a second later whatever happened. Now
 * that a session a Team Budget payment was taken for can't be deleted (the server
 * answers 409 and says why), the page reloads only once the delete succeeded: a
 * refused or failed delete keeps the page and its session, and the rep is told why —
 * a fresh start would hide the payment and invite paying again.
 */
export const useStartOver = ({
  resetSession,
  sessionId,
  then,
  reload = () => window.location.reload(),
}: UseStartOverArgs) => {
  'use memo'

  const { message } = App.useApp()
  const [isStartingOver, setIsStartingOver] = useState(false)
  // Synchronous: a second click before the next render must not delete twice.
  const inFlight = useRef(false)

  const startOver = async () => {
    if (inFlight.current) {
      return
    }
    inFlight.current = true
    setIsStartingOver(true)

    try {
      await resetSession(sessionId)
    } catch (error) {
      inFlight.current = false
      setIsStartingOver(false)
      message.error(refusalMessage(error))
      return
    }

    then?.()
    reload()
  }

  return { startOver, isStartingOver }
}
